import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Clock, AlertTriangle, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { useTrialStatus, isTrialActive, isTrialBenefitsExhausted, isSubscribedDuringTrial, hasTrialExtras } from '@/hooks/useTrialStatus';
import type { TrialStatus } from '@/hooks/useTrialStatus';

/**
 * Contextual trial banner displayed on Dashboard and Billing pages.
 * Shows different states: active trial, benefits exhausted, subscribed during trial, trial extras active.
 */
export function TrialBanner() {
  const { data: trial, isLoading } = useTrialStatus();

  if (isLoading || !trial?.has_trial) return null;

  // Already fully converted and no extras remain — no banner needed
  if (trial.trial_status === 'converted' && !hasTrialExtras(trial)) return null;
  if (trial.trial_status === 'expired' || trial.trial_status === 'cancelled') return null;

  // State: subscribed during trial with extras still active
  if (isSubscribedDuringTrial(trial) && hasTrialExtras(trial)) {
    return <SubscribedWithExtras trial={trial} />;
  }

  // State: subscribed during trial, converted
  if (isSubscribedDuringTrial(trial) && trial.trial_status === 'converted') {
    return null; // Clean transition, no banner needed
  }

  // State: trial benefits exhausted (not yet subscribed)
  if (isTrialBenefitsExhausted(trial)) {
    return <BenefitsExhausted trial={trial} />;
  }

  // State: active trial
  if (isTrialActive(trial)) {
    return <ActiveTrial trial={trial} />;
  }

  return null;
}

function ActiveTrial({ trial }: { trial: TrialStatus }) {
  const daysLeft = trial.days_remaining ?? 0;
  const isLow = daysLeft <= 3;

  return (
    <Card className={`rounded-xl border-s-4 ${isLow ? 'border-s-warning bg-warning/5' : 'border-s-primary bg-primary/5'}`}>
      <CardContent className="p-4 flex items-start gap-3">
        {isLow ? (
          <Clock className="w-5 h-5 text-warning mt-0.5 shrink-0" />
        ) : (
          <Sparkles className="w-5 h-5 text-primary mt-0.5 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-foreground">
              {isLow ? 'Trial Ending Soon' : 'Welcome to Your Free Trial'}
            </p>
            <Badge variant="outline" className="text-xs">
              {daysLeft} {daysLeft === 1 ? 'day' : 'days'} remaining
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {isLow
              ? 'Your trial is ending soon. Subscribe now to keep your access and carry over any remaining trial benefits.'
              : 'Explore ForkHire with full access to core features. Subscribe anytime — remaining trial value will be added on top of your plan.'}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" className="rounded-xl" asChild>
              <Link to="/settings/billing">
                <ArrowUpRight className="w-4 h-4 me-1.5" />
                Subscribe Now
              </Link>
            </Button>
            {trial.daily_caps && (
              <TrialUsageSummary trial={trial} />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BenefitsExhausted({ trial }: { trial: TrialStatus }) {
  const daysLeft = trial.days_remaining ?? 0;

  return (
    <Card className="rounded-xl border-s-4 border-s-destructive bg-destructive/5">
      <CardContent className="p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-destructive">Trial Benefits Consumed</p>
            {daysLeft > 0 && (
              <Badge variant="outline" className="text-xs">
                {daysLeft} trial {daysLeft === 1 ? 'day' : 'days'} remaining
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            You've used all available trial services. Subscribe to a paid plan to continue using ForkHire without interruption.
            {daysLeft > 0 && ' Your remaining trial days will be added to your first billing cycle.'}
          </p>
          <Button size="sm" className="rounded-xl mt-3" asChild>
            <Link to="/settings/billing">
              <ArrowUpRight className="w-4 h-4 me-1.5" />
              Choose a Plan
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SubscribedWithExtras({ trial }: { trial: TrialStatus }) {
  const extraDays = trial.trial_extra_days ?? 0;
  const extras = trial.trial_extra_allowances ?? {};
  const extraServices = Object.entries(extras).filter(([, v]) => v > 0);

  return (
    <Card className="rounded-xl border-s-4 border-s-success bg-success/5">
      <CardContent className="p-4 flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-success mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground">Subscription Active — Trial Bonus Applied</p>
          <p className="text-sm text-muted-foreground mt-1">
            Your paid plan is active and includes bonus allowances from your trial period.
            {extraDays > 0 && ` Your first renewal is extended by ${extraDays} extra ${extraDays === 1 ? 'day' : 'days'}.`}
          </p>
          {extraServices.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {extraServices.map(([key, count]) => (
                <Badge key={key} variant="secondary" className="text-xs">
                  +{count} {formatServiceKey(key)}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TrialUsageSummary({ trial }: { trial: TrialStatus }) {
  const caps = trial.daily_caps ?? {};
  const used = trial.today_usage ?? {};
  const entries = Object.entries(caps).filter(([, cap]) => cap > 0);
  if (entries.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {entries.slice(0, 3).map(([key, cap]) => {
        const usedCount = used[key] ?? 0;
        return (
          <Badge key={key} variant="outline" className="text-[10px]">
            {formatServiceKey(key)}: {usedCount}/{cap}
          </Badge>
        );
      })}
    </div>
  );
}

const SERVICE_LABELS: Record<string, string> = {
  job_management: 'Jobs',
  ai_cv_scoring: 'CV Scores',
  ai_interviews: 'Interviews',
  team_management: 'Seats',
};

function formatServiceKey(key: string): string {
  return SERVICE_LABELS[key] || key.replace(/_/g, ' ');
}
